import { useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { useSeedDetail } from '@/hooks/use-seeds';
import { useAuthContext } from '@/contexts/auth-context';
import { SeedStatsCards } from '@/components/seeds/seed-stats-cards';
import { EditSeedDialog } from '@/components/seeds/edit-seed-dialog';
import { DeleteSeedDialog } from '@/components/seeds/delete-seed-dialog';
import { AssignPracticeDialog } from '@/components/assignments/assign-practice-dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, Pencil, Trash2 } from 'lucide-react';
import { formatDate } from '@/lib/utils';

export default function SeedDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const { data: seed, isLoading } = useSeedDetail(id ?? '');
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <div className="grid gap-4 md:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
      </div>
    );
  }

  if (!seed) {
    return <p className="text-muted-foreground">Seed not found.</p>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/seeds')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h2 className="text-2xl font-bold">{seed.title}</h2>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Badge variant="outline">{seed.problem_category}</Badge>
              <span>#{seed.seed_number}</span>
              <span>{seed.estimated_minutes} min</span>
              <Badge variant="secondary">Difficulty {seed.difficulty_level}/5</Badge>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <AssignPracticeDialog
            fixedSeed={{
              id: seed.id,
              title: seed.title,
              problem_category: seed.problem_category,
              estimated_minutes: seed.estimated_minutes,
              difficulty_level: seed.difficulty_level,
            }}
            triggerLabel="Assign to Student"
            triggerSize="sm"
          />
          {user?.role === 'admin' && (
            <>
              <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
                <Pencil className="mr-2 h-4 w-4" />
                Edit
              </Button>
              <Button variant="destructive" size="sm" onClick={() => setDeleteOpen(true)}>
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </Button>
            </>
          )}
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Description</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="whitespace-pre-wrap">{seed.description}</p>
        </CardContent>
      </Card>

      <SeedStatsCards
        timesAssigned={seed.times_assigned}
        timesCompleted={seed.times_completed}
        completionRate={seed.completion_rate}
        personalizedCount={seed.personalized_count}
        avgDifficultyRating={seed.avg_difficulty_rating}
        sessionsByStatus={seed.sessions_by_status ?? {}}
      />

      <Card>
        <CardHeader>
          <CardTitle>Recent Assignments</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Student</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Assigned</TableHead>
                  <TableHead>Completed</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(seed.recent_assignments ?? []).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-muted-foreground">
                      No assignments yet.
                    </TableCell>
                  </TableRow>
                ) : (
                  (seed.recent_assignments ?? []).map((a) => (
                    <TableRow key={a.id}>
                      <TableCell className="font-medium">{a.student_name}</TableCell>
                      <TableCell>
                        <Badge variant={a.status === 'completed' ? 'default' : 'outline'}>
                          {a.status}
                        </Badge>
                      </TableCell>
                      <TableCell>{formatDate(a.assigned_at)}</TableCell>
                      <TableCell>{a.completed_at ? formatDate(a.completed_at) : '—'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {user?.role === 'admin' && (
        <>
          <EditSeedDialog
            seed={seed}
            open={editOpen}
            onOpenChange={setEditOpen}
            categories={[seed.problem_category]}
          />
          <DeleteSeedDialog
            seedId={seed.id}
            seedTitle={seed.title}
            open={deleteOpen}
            onOpenChange={setDeleteOpen}
            onDeleted={() => navigate('/seeds')}
          />
        </>
      )}
    </div>
  );
}
